// Consultas y claves de TanStack Query. La caché no es la fuente de verdad: los avisos en vivo
// (socket.ts) invocan `refreshConversation`, y queda un sondeo de respaldo (pollInterval) por si se pierde
// alguno: cada 60 s con el canal en vivo, cada 10 s sin él. También se refresca al volver a la pestaña.
import type {
  ConnectionView, ConversationState, ConversationView, MessageView, Page, SendPolicy, UserView,
} from '@hilo/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { api } from './api';
import { liveState } from './socket';

export const POLL_MS = 10_000;
export const LIVE_POLL_MS = 60_000;

/** Intervalo del sondeo de respaldo según el canal en vivo. */
export function pollInterval(): number {
  return liveState() === 'live' ? LIVE_POLL_MS : POLL_MS;
}
const MESSAGE_PAGE_LIMIT = 100;
/** Tope de páginas por hilo (10.000 mensajes): los mensajes se paginan del más antiguo al más reciente. */
const MESSAGE_MAX_PAGES = 100;

export type AssignedFilter = 'me' | 'free' | 'all';

export interface InboxFilters {
  connectionId?: string;
  state?: ConversationState;
  assigned: AssignedFilter;
  q?: string;
}

export const keys = {
  session: ['session'] as const,
  connections: ['connections'] as const,
  users: ['users'] as const,
  inbox: ['inbox'] as const,
  inboxList: (filters: InboxFilters) => ['inbox', filters] as const,
  conversation: (id: string) => ['conversation', id] as const,
  messages: (id: string) => ['conversation', id, 'messages'] as const,
  policy: (id: string) => ['conversation', id, 'policy'] as const,
};

export function fetchConnections(): Promise<ConnectionView[]> {
  return api<ConnectionView[]>('GET', '/connections');
}

export function fetchUsers(): Promise<UserView[]> {
  return api<UserView[]>('GET', '/users');
}

export function fetchInboxPage(filters: InboxFilters, cursor: string | null): Promise<Page<ConversationView>> {
  return api<Page<ConversationView>>('GET', '/conversations', {
    query: { ...filters, cursor, limit: 30 },
  });
}

export function fetchConversation(id: string): Promise<ConversationView> {
  return api<ConversationView>('GET', `/conversations/${id}`);
}

/** Todo el hilo en orden de recepción: recorre las páginas hasta el final. */
export async function fetchAllMessages(id: string, signal?: AbortSignal): Promise<MessageView[]> {
  const all: MessageView[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < MESSAGE_MAX_PAGES; page += 1) {
    const result: Page<MessageView> = await api<Page<MessageView>>('GET', `/conversations/${id}/messages`, {
      query: { cursor, limit: MESSAGE_PAGE_LIMIT },
      ...(signal ? { signal } : {}),
    });
    all.push(...result.items);
    if (!result.nextCursor) break;
    cursor = result.nextCursor;
  }
  // La paginación es una vista cambiante: deduplicar por ID.
  return orderThread([...new Map(all.map((message) => [message.id, message])).values()]);
}

/**
 * Orden del hilo: el de la API (receivedAt) y, si dos mensajes llegaron en el mismo instante, el de su
 * hora real (occurredAt). Sin ese desempate, un empate de receivedAt se ordena por ID, que es aleatorio.
 */
export function orderThread(messages: MessageView[]): MessageView[] {
  return messages
    .map((message, index) => ({ message, index }))
    .sort((a, b) => a.message.receivedAt.localeCompare(b.message.receivedAt)
      || a.message.occurredAt.localeCompare(b.message.occurredAt)
      || a.index - b.index)
    .map(({ message }) => message);
}

export function fetchPolicy(id: string): Promise<SendPolicy> {
  return api<SendPolicy>('GET', `/conversations/${id}/send-policy`);
}

/** Una revisión menor nunca sobrescribe una mayor ya conocida. */
export function storeConversation(client: QueryClient, view: ConversationView): void {
  client.setQueryData<ConversationView>(keys.conversation(view.id), (current) =>
    current && current.revision > view.revision ? current : view);
}

export async function refreshConversation(client: QueryClient, id: string): Promise<void> {
  await Promise.all([
    client.invalidateQueries({ queryKey: keys.conversation(id) }),
    client.invalidateQueries({ queryKey: keys.inbox }),
  ]);
}

export async function refreshInbox(client: QueryClient): Promise<void> {
  await client.invalidateQueries({ queryKey: keys.inbox });
}
